/**
 * Tenants API Service
 *
 * Type-safe tenant management using openapi-fetch typed client.
 * Types derived from platform-admin.openapi.yaml -- no manual interfaces.
 *
 * Endpoints consumed (via platform-admin spec):
 *   GET    /tenants              - List tenants (admin lookups, filtering)
 *   GET    /tenants/accessible   - RBAC-aware "tenants this user can enter"
 *   GET    /tenants/{tenantId}   - Get tenant detail
 */

import { platformAdminClient } from '../client';
import { readCsrfCookie } from '../../lib/csrf';
import type { components } from '@aaimplatform/institutional-types/platform-admin';

export type Tenant = components['schemas']['Tenant'];
export type TenantDetail = components['schemas']['TenantDetail'];
export type TenantListResponse = components['schemas']['TenantListResponse'];
export type TenantStatus = components['schemas']['TenantStatus'];
export type TenantType = components['schemas']['InstitutionType'];
export type ImpersonateTenantRequest = components['schemas']['ImpersonateTenantRequest'];
export type PlatformLoginResponse = components['schemas']['PlatformLoginResponse'];

export const tenantsApi = {
  list: async (params?: {
    search?: string;
    status?: string;
    type?: string;
  }): Promise<TenantListResponse> => {
    const { data, error } = await platformAdminClient.GET('/tenants', {
      params: {
        query: {
          search: params?.search,
          status: params?.status as TenantStatus | undefined,
          type: params?.type as TenantType | undefined,
        },
      },
    });
    if (error) throw new Error('Failed to fetch tenants');
    return data;
  },

  get: async (id: string): Promise<TenantDetail> => {
    const { data, error } = await platformAdminClient.GET('/tenants/{tenantId}', {
      params: { path: { tenantId: id } },
    });
    if (error) throw new Error('Failed to fetch tenant');
    return data;
  },

  /**
   * RBAC-aware accessible-tenants lookup. Returns the set of tenants the
   * calling user can enter, used to drive the OrgSwitcher visibility and
   * contents. Platform admins see apex + all FI tenants; FI users see their
   * own tenant only (multi-org FI is C12FR->C13-6 follow-on).
   */
  accessible: async (): Promise<TenantListResponse> => {
    const { data, error } = await platformAdminClient.GET('/tenants/accessible', {});
    if (error) throw new Error('Failed to fetch accessible tenants');
    return data;
  },

  /**
   * Mint a tenant impersonation token. Calls the platform-admin-api proxy
   * which forwards to aaim-core /api/v1/auth/impersonate. Caller must hold
   * platform_admin or aaim_super_admin role.
   *
   * Note: this endpoint lives at /api/platform/auth/impersonate (NOT
   * /api/admin/...), so it is not reachable through the platformAdminClient
   * (whose baseUrl is /api/admin). We use a direct fetch authenticated by the
   * HttpOnly session cookie (credentials: 'include'); the impersonate response
   * also rotates the session cookie to the impersonated context. N6 / SEC-V04 --
   * the SPA holds no bearer to attach.
   *
   * Pass the apex tenant id to return to the apex context. The response in
   * that case has no `impersonatedBy` field on the user.
   */
  impersonate: async (
    request: ImpersonateTenantRequest,
  ): Promise<PlatformLoginResponse> => {
    // CSRF double-submit (N8 / GH #1806). This POST authenticates by the
    // session cookie and sends no Authorization header, and platform-admin-api
    // now mounts its auth router BELOW a gate carrying `enforceCsrf: true`, so
    // `passesCsrfCheck` in
    // packages/database/src/security/jwt-auth-middleware.ts refuses a mutating
    // cookie-authenticated request with no echoed header: 403
    // CSRF_VALIDATION_FAILED, which presents as the OrgSwitcher silently
    // failing to change tenant. Per the deploy-order clause of
    // .claude/rules/cookie-session-auth-canon.md this echo ships BEFORE the
    // service-side enforcement it depends on.
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };
    const csrf = readCsrfCookie();
    if (csrf) headers['X-CSRF-Token'] = csrf;

    const response = await fetch('/api/platform/auth/impersonate', {
      method: 'POST',
      headers,
      credentials: 'include',
      body: JSON.stringify(request),
    });
    if (!response.ok) {
      const errBody = await response.json().catch(() => null) as
        | { error?: { code?: string; message?: string } }
        | null;
      const code = errBody?.error?.code ?? 'IMPERSONATION_FAILED';
      const message = errBody?.error?.message ?? 'Failed to impersonate tenant';
      throw new Error(`${code}: ${message}`);
    }
    return (await response.json()) as PlatformLoginResponse;
  },
};
